/**
 * #700 — WhatsApp Web connection breaker.
 *
 * Owner 6adeadf8's tab kept reporting `connected` while WhatsApp Web's socket was closed: every send threw
 * "AbortError: The connection was closed.", and every scheduler wake (MQTT/FCM, ~3 min apart) ran a real
 * attempt and bumped retryCount, so 273 campaign messages each burned their whole retry budget (6 attempts,
 * ~67 log rows apiece) and were failed as `system`. The page's own status gate never fired because the page
 * never said it was down.
 *
 * Once a send fails with a closed connection the breaker opens and the send thunks stop dispatching: no
 * runSendProtocol, no lock, no retryCount bump. They write a "WhatsApp not connected - …" row instead, the
 * same wording the #449 gate uses. The message stays pending; this only stops the attempt and retry burn.
 *
 * The breaker closes when:
 *  - the page reports it is NOT connected and later connected again (the stream really came back), or
 *  - a probe send gets past the socket. While open, one message at a time may probe after a cooldown that
 *    doubles from `probeCooldownMs` up to `maxProbeCooldownMs`; the probe is leased so a thunk that never
 *    settles it can't hold the breaker shut.
 */

/**
 * #1114 — WhatsApp Web's comms layer not started (stream DISCONNECTED while the page still says CONNECTED) throws
 * "[comms] sendIq called before startComms". Same class as a closed socket: nothing reached the wire, the
 * page will come back by itself, so it opens the breaker instead of burning retries as a terminal failure.
 */
export const CONNECTION_CLOSED_ERROR_RE = /The connection was closed|sendIq called before startComms/i;

export const isConnectionClosedError = (error: unknown): boolean =>
    typeof error === 'string' && CONNECTION_CLOSED_ERROR_RE.test(error);

/** `pass`: send normally. `probe`: breaker open, this message may try once. `blocked`: don't dispatch. */
export type BreakerDecision = 'pass' | 'probe' | 'blocked';

export interface BreakerConfig {
    probeCooldownMs?: number;
    maxProbeCooldownMs?: number;
    probeLeaseMs?: number;
    now?: () => number;
}

interface OpenState {
    openedAt: number;
    cooldownMs: number;
    nextProbeAt: number;
    sawDisconnected: boolean;
    probe?: { messageId: string; until: number };
}

export class WaConnectionBreaker {
    private open: OpenState | null = null;

    constructor(private readonly cfg: BreakerConfig = {}) {}

    private get probeCooldownMs(): number { return this.cfg.probeCooldownMs ?? 60_000; }
    private get maxProbeCooldownMs(): number { return this.cfg.maxProbeCooldownMs ?? 10 * 60_000; }
    private get probeLeaseMs(): number { return this.cfg.probeLeaseMs ?? 5 * 60_000; }
    private now(): number { return (this.cfg.now ?? Date.now)(); }

    isOpen(): boolean { return this.open !== null; }

    /** Back to closed (a fresh page; tests). */
    reset(): void { this.open = null; }

    /** How long the breaker has been open, or 0. */
    openForMs(): number { return this.open ? this.now() - this.open.openedAt : 0; }

    /**
     * May this message dispatch? Re-entrant per message: the retry thunk asks before it bumps retryCount and
     * the send thunk it dispatches asks again; the same message keeps its probe.
     */
    decide(messageId: string): BreakerDecision {
        const open = this.open;
        if (!open) return 'pass';
        const now = this.now();
        if (open.probe && open.probe.until <= now) open.probe = undefined;
        if (open.probe) return open.probe.messageId === messageId ? 'probe' : 'blocked';
        if (now < open.nextProbeAt) return 'blocked';
        open.probe = { messageId, until: now + this.probeLeaseMs };
        return 'probe';
    }

    /** The page's own status reading. Down then up again means the stream reconnected: close. */
    observeConnection(connected: boolean): void {
        if (!this.open) return;
        if (!connected) this.open.sawDisconnected = true;
        else if (this.open.sawDisconnected) this.open = null;
    }

    /** A send's outcome. A closed connection opens (or re-opens, with a longer cooldown) the breaker. */
    recordSendOutcome(messageId: string, outcome: { settled: boolean; connectionClosed: boolean }): void {
        const now = this.now();
        if (outcome.connectionClosed) {
            const open = this.open;
            if (!open) {
                this.open = { openedAt: now, cooldownMs: this.probeCooldownMs, nextProbeAt: now + this.probeCooldownMs, sawDisconnected: false };
                return;
            }
            if (open.probe?.messageId === messageId) {
                open.cooldownMs = Math.min(open.cooldownMs * 2, this.maxProbeCooldownMs);
                open.probe = undefined;
            }
            open.nextProbeAt = now + open.cooldownMs;
            return;
        }
        // Any other settled outcome (sent, or a failure WhatsApp answered differently) means the socket works.
        if (outcome.settled) {
            this.open = null;
            return;
        }
        // No verdict (lock denied, aborted before the send): hand the probe back, the next message may try.
        if (this.open?.probe?.messageId === messageId) this.open.probe = undefined;
    }
}

/** One breaker per content-script lifetime == per WhatsApp Web page. */
export const waConnectionBreaker = new WaConnectionBreaker();

/**
 * The deferral row. `prefix` keeps each thunk's existing not-connected wording ("WhatsApp not connected" /
 * "Retry skipped - WhatsApp not connected"), so the row reads like the #449 gate's.
 */
export const waDisconnectedDeferLog = (prefix: string, openForMs: number): string =>
    `${prefix} - WhatsApp Web disconnected (send failed: connection closed), deferred without an attempt; down ${Math.round(openForMs / 60_000)}m (#700)`;
